import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Page, PageHeader, Segmented } from "../../../components/ui";
import { getNotificationsOverview } from "../../../services/notifications";
import Composer, { draftFrom } from "./Composer";
import History from "./History";
import Automations from "./Automations";
import "./notifications.css";

const TABS = [
  { value: "send", label: "Send", icon: "ri-send-plane-line" },
  { value: "history", label: "History", icon: "ri-file-list-3-line" },
  { value: "automations", label: "Automations", icon: "ri-repeat-line" },
];

const Stat = ({ icon, value, label, tone }) => (
  <div className="ui-card notif-stat" data-tone={tone}>
    <span className="notif-stat-icon" aria-hidden="true">
      <i className={icon}></i>
    </span>
    <div>
      <strong>{value ?? "—"}</strong>
      <span>{label}</span>
    </div>
  </div>
);

/** Push notifications of the app: send (everyone, users, group), history, automations. */
const Notifications = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("send");
  const [overview, setOverview] = useState(null);
  const [draft, setDraft] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const refreshOverview = useCallback(() => getNotificationsOverview().then(setOverview), []);
  useEffect(() => {
    refreshOverview();
  }, [refreshOverview]);

  const changed = () => {
    refreshOverview();
    setHistoryKey((k) => k + 1);
  };

  return (
    <Page>
      <PageHeader
        title={t("Notifications")}
        subtitle={t("Send push notifications to the app: to everyone, to a group or to a single user.")}
      />

      <div className="notif-stats">
        <Stat icon="ri-smartphone-line" value={overview?.reachableUsers} label={t("Logged-in users reachable")} tone="blue" />
        <Stat icon="ri-send-plane-line" value={overview?.sent30d} label={t("Sent in the last 30 days")} tone="green" />
        <Stat icon="ri-calendar-2-line" value={overview?.scheduled} label={t("Scheduled")} tone="violet" />
        <Stat icon="ri-error-warning-line" value={overview?.failed30d} label={t("Failed (30 days)")} tone="red" />
      </div>

      <div className="notif-tabs">
        <Segmented
          label={t("Notifications")}
          value={tab}
          onChange={setTab}
          options={TABS.map((item) => ({
            value: item.value,
            label: (
              <>
                <i className={item.icon} aria-hidden="true"></i> {t(item.label)}
              </>
            ),
          }))}
        />
      </div>

      {tab === "send" && (
        <Composer
          initialDraft={draft}
          onSent={(notification) => {
            setDraft(null);
            changed();
            if (notification.status === "scheduled") setTab("history");
          }}
        />
      )}
      {tab === "history" && (
        <History
          refreshKey={historyKey}
          onChange={changed}
          onDuplicate={(notification) => {
            setDraft(draftFrom(notification));
            setTab("send");
          }}
        />
      )}
      {tab === "automations" && <Automations onChange={changed} />}
    </Page>
  );
};

export default Notifications;
